import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDrop } from "react-dnd";
import {
  FormCreationData,
  DeviceType,
  SAVE_FORM_JSON,
  UPDATE_PARENT_FIELDS,
} from "../../../../../stateManager/store/formCreation_v2/types";
import { ElementWrapper } from "./ElementWrapper";
import MicroComponents from "./MicroComponents";
import { ItemTypes } from "../types/ItemTypes";
import { onDrop, handleContainers, getContainerField } from "../helpers";

import { containers, canvasElements } from "../constants";
import { getGridStyles, getMargin, getPadding } from "../helpers/generateStyles";
import { AppState } from "../../../../../stateManager/store";
import { useTopElement } from "../hooks/useTopElement";
import { useToast } from "../../../../../helpers/commonFunction/useToast";
import { setMultipleSelectionv2 } from "../../../../../stateManager/store/contextMenuv2/action";
import { addWrapperComputedStyles, setSelectedFormItemId, updatedSelectedeElements } from "../../../../../stateManager/store/formCreation_v2/actions";
import { clearSelectedItems } from "../../../../../stateManager/store/contextMenu/action";
import { HandleContainersV3 } from "../helpers/HandleLayoutComponentsv3";
import { MouseHoverOnTree } from "../../../../../helpers/RxServices";
import { calculatePosition } from "../../../../../utils/badgePosition";
import { wizardContentInitialState } from "../helpers/wizardHelpers";
import { store } from "../../../../../stateManager";
import classNames from "classnames";

interface IGrid {
  deviceType: DeviceType;
  element: FormCreationData;
  isCheckedIn?: Boolean;
  zoomScale?: any;
  children?: ReactNode
}

const horizontalAlignment = (alignX: string): string => {
  switch (alignX) {
    case "start":
      return "justify-self-start"
    case "end":
      return "justify-self-end"
    case "center":
      return "justify-self-center"
    default:
      return ""
  }
}

const verticalAlignment = (alignY: string): string => {
  switch (alignY) {
    case "start":
      return "self-start"
    case "end":
      return "elf-end"
    case "center":
      return "self-center"
    default:
      return ""
  }
}

export const Grid: React.FC<IGrid> = ({ isCheckedIn, element, deviceType, zoomScale, children }) => {
  const { selectedElementId, isCoreV3, components } = useSelector((state: any) => state.formJson);
  const [isTop, setIsTop] = useTopElement();
  const [hoveredElId, setHoveredElId] = useState<string>()
  const { WarningToast } = useToast()
  const { id, isAsset, isChildAsset, type, properties } = element;
  const assetEditModeEnabled = useSelector((state: AppState) => state.formJson.assetEditMode.isEnabled)
  const styles = element?.properties?.styles?.[deviceType] || {}
  const gridStyles = getGridStyles(styles)
  const paddingStyle = getPadding(styles)
  const fields: string [] = assetEditModeEnabled ? element.fields : components[id]?.fields;
  const newStyles: { [key: string]: any } = {
    ...element?.properties?.styles?.common,
    ...element?.properties?.styles?.[deviceType]
  }
  const { border, borderRadius, borderColor, backgroundColor, absolute, fontFamily = 'Inherit', heightMode, height, width, widthMode,visible } = newStyles || {};
  const backgroundImage = (newStyles?.backgroundImage?.url != undefined && newStyles?.backgroundImage?.url != '') && newStyles?.backgroundImage?.url
  const positionGen = absolute ? "absolute" : "relative";
  const dispatch = useDispatch();
  const visibilityGen = visible ? "block" : "hidden"

  const nonDroppableAssetTypes = ['FormWizard', 'FlatList'];
  const [{ isOverCurrent, canDrop }, drop] = useDrop({
    accept: [
      ItemTypes.ELEMENT,
      ItemTypes.COMPONENT,
      ItemTypes.ASSET,
      ItemTypes.TEMPLATE
    ],
    canDrop: (item: any) => {
      // 1. the source grid itself gets dropped
      if (id === item.json.id) {
        return false
      }

      if (item.json.type === 'Spacer' || canvasElements.includes(item.json.type)) {
        return false
      }


      // 2. the dragged item is already present in the grid
      if (item.type === ItemTypes.ELEMENT && item.json.parentId === id) {
        return false;
      }

      // 3. asset instance shouldn't drop source when
      // edit mode is turned off
      if (!assetEditModeEnabled && (isChildAsset || isAsset)) {
        return false
      }

      if (nonDroppableAssetTypes.includes(item?.json?.type) && assetEditModeEnabled) {
        return false
      }

      return true;
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      };
    },
    drop: async (item: any) => {
      // if (isOverCurrent) {
      //   onDrop({ id }, item, deviceType)
      // }
      if (isOverCurrent) {
        if (isCheckedIn) {
          let fieldId: any;
          if (item?.json?.type !== 'FormWizard') {
            fieldId = onDrop({ id }, item, deviceType, isCoreV3)
          }
          if (item?.json?.type === "TabBar") {
            let data: any = {
              type: "Component",
              json: {
                type: 'Tab',
                label: 'Tab 1',
                value: "tab 1"
              }
            }
            onDrop({ id: fieldId }, data, deviceType, isCoreV3)
          } else if (item?.json?.type === "FormWizard") {
            onDrop({ id }, {type: ItemTypes.FORMWIZARD, json: {}}, deviceType, isCoreV3);
          }
        } else {
          WarningToast("Please Checkout The Form!!")
        }
      }
    },
  });
  
  function kebabToCamel(kebab: any) {
    return kebab.replace(/-([a-z])/g, (match: any, letter: any) => letter.toUpperCase());
  }

  useEffect(() => {
    setIsTop(id)
  }, [id, setIsTop])

  useEffect(() => {
    const subscription = MouseHoverOnTree.getMessage().subscribe(function (e) {
      setHoveredElId(e);
    });
    if (!subscription) {
      MouseHoverOnTree.getMessage();
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [hoveredElId]);

  const isActive = isOverCurrent && canDrop;
  const animationClass = isActive ? "drop-active" : canDrop ? "drop-passive" : "";

  const isGridEmpty = fields && fields.length === 0

  const elementStyle = properties?.styles || {};
  const marginStyle = getMargin(elementStyle[deviceType])

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    if (e.shiftKey && !isChildAsset) {
      dispatch(setMultipleSelectionv2(true));
      dispatch(updatedSelectedeElements(id));
    } else {
      dispatch(setMultipleSelectionv2(false));
      dispatch(
        setSelectedFormItemId({
          id,
          isAssetInstanceSelected: isAsset || isChildAsset,
        })
      );
      dispatch(clearSelectedItems())
    }
  };

  const handleRightClick = async (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    const existingWrapper = document.getElementById("wrapper");
    existingWrapper && await existingWrapper.remove();
    await dispatch(
      setSelectedFormItemId({
        id,
        isAssetInstanceSelected: isAsset || isChildAsset,
      })
    );
    await dispatch(clearSelectedItems());
  };

  const getHeight = (): string => {
    let returnVal = "60px";
    if (heightMode === 'auto') {
      returnVal = "auto"
    } else if (heightMode === 'min-content') {
      returnVal = "min-content"
    } else if (heightMode === 'max-content') {
      returnVal = "max-content"
    } else if (heightMode === '%' || heightMode === 'px') {
      returnVal = `${height}${heightMode}`
    }
    return returnVal
  }

  const getWidth = (): string => {
    if (widthMode === 'auto') {
      return "fit-content"
    } else if (widthMode === 'min-content') {
      return "min-content"
    } else if (widthMode === 'max-content') {
      return "max-content"
    } else if (width) {
      return `${width}${newStyles.widthMode}`;
    }
    return "100%"
  }

  useEffect(() => {
    if (selectedElementId === id && (components[selectedElementId]?.type === "Grid")) {
      const childElement = document.getElementById(`${type}-${id}`)
      if (childElement) {
        const computedStyles = window.getComputedStyle(childElement);
        const camelCaseStyles: any = {};

        for (let i = 0; i < computedStyles.length; i++) {
          const prop = computedStyles[i];
          const value = computedStyles.getPropertyValue(prop);
          const camelProp = kebabToCamel(prop);
          camelCaseStyles[camelProp] = value;
        }
        const selectedStyles = {
          width: camelCaseStyles.width,
          height: camelCaseStyles.height,
          marginTop: camelCaseStyles.marginTop,
          marginBottom: camelCaseStyles.marginBottom,
          marginLeft: camelCaseStyles.marginLeft,
          marginRight: camelCaseStyles.paddingRight,
          position: camelCaseStyles.position,
          lineHeight: camelCaseStyles.lineHeight,
          backgroundColor: computedStyles.getPropertyValue("background-color"),
          borderColor: computedStyles.getPropertyValue("border-color")
        };
        dispatch(addWrapperComputedStyles(selectedStyles))
      }
    }
  }, [selectedElementId])
  const combinedClassName = properties?.config?.classNameTags?.join(" ") || [];
  return (
    <>
      {isCoreV3 ?
        <div
          className={`${type}-wrapper ${combinedClassName} ${positionGen} ${visibilityGen} ${horizontalAlignment(newStyles?.alignX)} ${verticalAlignment(newStyles?.alignY)}`}
          style={{
            fontFamily: fontFamily,
            height: getHeight(),
            width: getWidth(),
            ...marginStyle,
            position: 'relative'
          }}
          id={`${type}-${id}`}
          ref={ref => drop(ref)}>

          <div
            ref={ref => drop(ref)}
            onMouseOver={(e) => {
              e.stopPropagation();
              MouseHoverOnTree.sendMessage(id)
            }}
            onMouseLeave={() => {
              MouseHoverOnTree.sendMessage(null)
            }}
            id={`${id}`}
            className={`${type}-wrapperPadding grid  ${gridStyles} ${horizontalAlignment(newStyles?.alignX)} ${verticalAlignment(newStyles?.alignY)} ${animationClass} ${type}-wrapper`}
            onClick={(e) => handleSelect(e, id)}
            onContextMenu={(e) => {
              e.preventDefault();
              handleRightClick(e,id);
            }}
            style={{
              ...paddingStyle,
              borderWidth: border ? `${border}px` : undefined,
              borderRadius: borderRadius ? `${borderRadius}px` : undefined,
              borderColor: borderColor ? `${borderColor}` : undefined,
              backgroundColor: hoveredElId === id ? "#EFF0F4": backgroundColor ? `${backgroundColor}` : isGridEmpty ? '#f1f1f1' : '#fff',
              gap: styles?.gap ? `${styles?.gap}px` : '0px',
              minHeight: getHeight(),
              height: '100%',
              width: '100%',
              overflowX: newStyles?.overflowX ? newStyles?.overflowX : "hidden",
              overflowY: newStyles?.overflowY ? newStyles?.overflowY : "hidden",
              gridAutoRows: "min-content",
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: newStyles?.backgroundImage?.size || 'cover',
              backgroundRepeat: newStyles?.backgroundImage?.repeat || 'no-repeat',
            }}
          >{children ? children : null}
            {fields?.map((fieldId, index) => {
              const elements = getContainerField(fieldId, element)
              if (!elements) {
                return null
              }
              const { component, parentComponent } = elements
              return (
                <React.Fragment key={fieldId}>
                  {containers.includes(component.type) ? (
                    <HandleContainersV3 isCheckedIn={isCheckedIn} element={component} deviceType={deviceType} index={index}/>
                  ) : (
                    <MicroComponents index={index} element={component} deviceType={deviceType} zoomScale={zoomScale} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        :
        <div style={{ fontFamily: fontFamily }}>
          {selectedElementId === id && type !== "FAB" && type !== "A8Modal" && <p
            data-test-id={id}
            id={`tag_${id}`}
            style={{ ...marginStyle }}
            className={`${combinedClassName}` &&type === "SnackBar" ? "fixed bg-palered text-sm text-white px-1 rounded-sm bottom-14 z-50 left-4 mt-3" : ` absolute bg-palered flex items-center text-sm text-white px-1 rounded h-6 bottom-0 z-50 left-0`}>
            {id}</p>
          }
          <div
            ref={ref => drop(ref)}
            id={`${type}-${id}`}
            className={`grid ${gridStyles} grid-items ${animationClass} a8-grid`}
            style={{
              ...paddingStyle,
              borderWidth: border ? `${border}px` : undefined,
              borderRadius: borderRadius ? `${borderRadius}px` : undefined,
              borderColor: borderColor ? `${borderColor}` : undefined,
              backgroundColor: hoveredElId === id ? "#EFF0F4" : backgroundColor ? `${backgroundColor}` : isGridEmpty ? '#f1f1f1' : '#fff',
              gap: styles?.gap ? `${styles?.gap}px` : '0px',
              minHeight: styles?.height ? `${styles.height}${styles.heightMode}` : '30px',
              width: styles?.width ? `${styles.width}${styles.widthMode}` : '100%',
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: newStyles?.backgroundImage?.size || 'cover',
              backgroundRepeat: newStyles?.backgroundImage?.repeat || 'no-repeat',
            }}
          >
            {fields?.map((fieldId, index) => {
              const elements = getContainerField(fieldId, element)
              if (!elements) {
                return null
              }
              const { component, parentComponent } = elements
              return (
                <ElementWrapper key={component.id} isCheckedIn={isCheckedIn} element={component} index={index} parentElement={parentComponent} >
                  <div>
                    {containers.includes(component.type) ? (
                      handleContainers({ isCheckedIn, element: component, deviceType })
                    ) : (
                      <MicroComponents element={component} deviceType={deviceType} zoomScale={zoomScale} />
                    )}
                  </div>
                </ElementWrapper>
              );
            })}
          </div>
        </div>
      }
    </>
  );
}
export const GridWithBadge = (props: IGrid) => {
  const { element: { properties: { styles: { common }, config: { enableBadge } } } } = props;
  const { badge: { position, customHorizontal, customVertical, badgeBindBadge, WidthHeigthh = 25, WidthHeigthw = 25, defaultValue, background, textColor, badgeFontSize, badgeBorderRadius, borderWidth, badgeBorderColor } } = common;
  let pos = calculatePosition(position, { customHorizontal, customVertical });

  const badgeStyle: React.CSSProperties = {
    background: background,
    borderRadius: badgeBorderRadius,
    borderColor: badgeBorderColor,
    borderWidth: `${borderWidth}px`,
    fontSize: `${badgeFontSize}px`,
    color: textColor,
    width: WidthHeigthw ? `${WidthHeigthw}px` : "25px",
    height: WidthHeigthh ? `${WidthHeigthh}px` : "25px",
    zIndex: 999
  }

  return <Grid {...props}>
    {enableBadge && <div className={`absolute ${pos} flex items-center justify-center`} style={badgeStyle} >{defaultValue}</div>}
  </Grid>
}